import React, { useEffect, useState } from "react";

import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  Skeleton,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper
} from "@mui/material";

import { getProjects } from "../../services/projectService";
import { getEmployees } from "../../services/employeeService";

function ResourceCalendar({ employeeId, onCapacityChange }) {
  const [projects, setProjects] = useState([]);
  const [employee, setEmployee] = useState(null);
  const [loading, setLoading] = useState(false);
  const [totalCapacity, setTotalCapacity] = useState(0);

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);

        if (employeeId) {
          const projectsData = await getProjects("", employeeId);
          setProjects(projectsData);

          const employeesData = await getEmployees("", null, null);
          const emp = employeesData.find((e) => e.employee_id === employeeId);
          setEmployee(emp);

          const capacity = projectsData.reduce((sum, p) => sum + (p.capacity_percent || 0), 0);
          setTotalCapacity(Math.min(capacity, 100));

          if (onCapacityChange) {
            onCapacityChange(Math.min(capacity, 100));
          }
        }
      } catch (error) {
        console.error("Error loading calendar data:", error);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [employeeId, onCapacityChange]);

  const getCapacityColor = (capacity) => {
    if (capacity >= 100) return "#ef4444";
    if (capacity >= 75) return "#f97316";
    if (capacity >= 50) return "#eab308";
    return "#22c55e";
  };

  const formatDate = (date) => {
    if (!date) return "-";
    return new Date(date).toLocaleDateString("en-US", {
      year: "2-digit",
      month: "short",
      day: "numeric"
    });
  };

  if (loading) {
    return (
      <Box>
        {/* Capacity card skeleton */}
        <Card sx={{ borderRadius: 2, mb: 2, background: "#f8fafc" }}>
          <CardContent>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Box>
                <Skeleton variant="text" width={120} height={20} />
                <Skeleton variant="text" width={160} height={28} sx={{ mt: 0.5 }} />
              </Box>
              <Skeleton variant="circular" width={60} height={60} />
            </Box>
          </CardContent>
        </Card>

        {/* Table skeleton */}
        <Card sx={{ borderRadius: 2 }}>
          <CardContent sx={{ pb: 1 }}>
            <Skeleton variant="text" width={180} height={28} sx={{ mb: 1.5 }} />
            <Box>
              {/* Header row */}
              <Box display="flex" gap={1} mb={1}>
                {[160, 100, 100, 90].map((w, i) => (
                  <Skeleton key={i} variant="rounded" width={w} height={22} />
                ))}
              </Box>
              {/* Data rows */}
              {[1, 2, 3].map((i) => (
                <Box key={i} display="flex" gap={1} mb={1} alignItems="center">
                  <Skeleton variant="text" width={160} height={22} />
                  <Skeleton variant="rounded" width={100} height={22} />
                  <Skeleton variant="rounded" width={100} height={22} />
                  <Skeleton variant="rounded" width={90} height={22} />
                </Box>
              ))}
            </Box>
          </CardContent>
        </Card>
      </Box>
    );
  }

  if (!employeeId) {
    return (
      <Card sx={{ borderRadius: 2, background: "#f8fafc" }}>
        <CardContent>
          <Typography color="textSecondary">
            Select an employee to view resource calendar
          </Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Box>
      <Card sx={{ borderRadius: 2, mb: 2, background: "#f8fafc" }}>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Box>
              <Typography variant="subtitle2" color="textSecondary">
                {employee?.employee_name || "Employee"}
              </Typography>
              <Typography variant="h6" fontWeight={600} mt={0.5}>
                Current Capacity
              </Typography>
            </Box>

            <Box display="flex" alignItems="center" gap={2}>
              <Box display="flex" alignItems="center" gap={1}>
                <Box
                  sx={{
                    width: 60,
                    height: 60,
                    borderRadius: "50%",
                    background: `conic-gradient(
                      ${getCapacityColor(totalCapacity)} 0deg ${(totalCapacity / 100) * 360}deg,
                      #e5e7eb ${(totalCapacity / 100) * 360}deg
                    )`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center"
                  }}
                >
                  <Typography variant="h6" fontWeight={700} color={getCapacityColor(totalCapacity)}>
                    {totalCapacity}%
                  </Typography>
                </Box>
              </Box>

              <Box>
                <Typography variant="body2" color="textSecondary">
                  {totalCapacity}% allocated
                </Typography>
                <Typography variant="body2" fontWeight={600}>
                  {100 - totalCapacity}% available
                </Typography>
              </Box>
            </Box>
          </Box>

          {totalCapacity >= 100 && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              Employee is fully allocated (100% capacity)
            </Alert>
          )}
        </CardContent>
      </Card>

      {projects.length > 0 && (
        <Card sx={{ borderRadius: 2 }}>
          <CardContent>
            <Typography variant="subtitle2" fontWeight={600} mb={2}>
              Assigned Projects ({projects.length})
            </Typography>

            <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ background: "#f8fafc" }}>
                    <TableCell sx={{ fontWeight: 600, fontSize: 12 }}>
                      Project
                    </TableCell>
                    <TableCell align="center" sx={{ fontWeight: 600, fontSize: 12 }}>
                      Capacity
                    </TableCell>
                    <TableCell align="center" sx={{ fontWeight: 600, fontSize: 12 }}>
                      Start
                    </TableCell>
                    <TableCell align="center" sx={{ fontWeight: 600, fontSize: 12 }}>
                      End
                    </TableCell>
                    <TableCell align="center" sx={{ fontWeight: 600, fontSize: 12 }}>
                      Status
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {projects.map((project) => {
                    const capacity = project.capacity_percent || 0;
                    const isFullyBooked = capacity >= 100;

                    return (
                      <TableRow key={project.project_id} hover>
                        <TableCell sx={{ fontSize: 13 }}>
                          <Typography fontWeight={500}>
                            {project.project_name}
                          </Typography>
                        </TableCell>

                        <TableCell align="center">
                          <Chip
                            label={`${capacity}%`}
                            size="small"
                            sx={{
                              background: getCapacityColor(capacity),
                              color: "#fff",
                              fontWeight: 600,
                              fontSize: 11
                            }}
                          />
                        </TableCell>

                        <TableCell align="center" sx={{ fontSize: 13 }}>
                          {formatDate(project.start_date)}
                        </TableCell>

                        <TableCell align="center" sx={{ fontSize: 13 }}>
                          {formatDate(project.end_date)}
                        </TableCell>

                        <TableCell align="center">
                          {isFullyBooked ? (
                            <Chip label="Fully Booked" size="small" color="error" variant="outlined" />
                          ) : capacity > 0 ? (
                            <Chip label="Active" size="small" color="success" variant="outlined" />
                          ) : (
                            <Chip label="Pending" size="small" variant="outlined" />
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}

      {projects.length === 0 && (
        <Card sx={{ borderRadius: 2, textAlign: "center", p: 3 }}>
          <Typography color="textSecondary">
            No projects assigned
          </Typography>
        </Card>
      )}
    </Box>
  );
}

export default ResourceCalendar;
